import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { UserStatus } from '$/core/enums';
import { ReactComponent as StatusAvailableIcon } from '$/assets/icons/status-available.svg';
import { ReactComponent as StatusBreakIcon } from '$/assets/icons/status-break.svg';
import { ReactComponent as StatusOutOfOfficeIcon } from '$/assets/icons/status-out-of-office.svg';
import { ReactComponent as StatusBusyIcon } from '$/assets/icons/status-busy.svg';
import { ReactComponent as StatusNoTasksIcon } from '$/assets/icons/status-no-tasks.svg';
import './index.scss';

export const StatusIcon: React.FC<{ status: UserStatus }> = ({ status }) => {
  switch (status) {
    case UserStatus.Available:
      return (
        <span className="status-icon available">
          <StatusAvailableIcon />
        </span>
      );
    case UserStatus.Break:
      return (
        <span className="status-icon break">
          <StatusBreakIcon />
        </span>
      );
    case UserStatus.OutOfOffice:
      return (
        <span className="status-icon out-of-office">
          <StatusOutOfOfficeIcon />
        </span>
      );
    case UserStatus.Busy:
      return (
        <span className="status-icon busy">
          <StatusBusyIcon />
        </span>
      );
    case UserStatus.NoTasks:
      return (
        <span className="status-icon notasks">
          <StatusNoTasksIcon />
        </span>
      );
    default:
      return null;
  }
};

export interface IUserStatusDropdownProps {
  currentStatus: UserStatus;
  onStatusChange: (status: UserStatus) => void;
  isUpdating?: boolean;
}

const UserStatusDropdown: React.FC<IUserStatusDropdownProps> = ({
  currentStatus,
  onStatusChange,
  isUpdating = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleStatusSelect = (status: UserStatus) => {
    onStatusChange(status);
    setIsOpen(false);
  };

  const statuses = [
    UserStatus.Available,
    UserStatus.Break,
    UserStatus.OutOfOffice,
    UserStatus.Busy,
    UserStatus.NoTasks,
  ];

  return (
    <div className="user-status-dropdown" ref={dropdownRef}>
      <button
        className={classNames('status-trigger', { active: isOpen, updating: isUpdating })}
        onClick={() => !isUpdating && setIsOpen(!isOpen)}
        disabled={isUpdating}
        aria-label={isUpdating ? 'Updating status…' : undefined}>
        {isUpdating ? <span className="status-spinner" aria-hidden="true" /> : <StatusIcon status={currentStatus} />}
        <span className={classNames('status-text', currentStatus.toLowerCase().replaceAll(/\s+/g, ''))}>
          {isUpdating ? 'Updating…' : currentStatus}
        </span>
        {!isUpdating && <span className="status-chevron">▼</span>}
      </button>

      {isOpen && (
        <div className="status-dropdown-menu">
          {statuses.map(status => (
            <button
              key={status}
              className={classNames('status-option', {
                selected: status === currentStatus,
                [status.toLowerCase().replaceAll(/\s+/g, '')]: true,
              })}
              onClick={() => handleStatusSelect(status)}>
              <StatusIcon status={status} />
              <span>{status}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserStatusDropdown;
